/** ONE Mail — the person's own email assistant: what needs them, triaged from their Microsoft 365 mailbox. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AttachmentChip, FilterBar, filterQs, FlagChips, MailProSwitch, MeetingCard, MoreMenu, OooBanner, OutOfOffice, SentList, SharedInbox, SharedMailboxes, Templates, type MailFilter } from "../components/mail/pro";
import { MailComposer } from "../components/mail/MailComposer";
import { Signatures } from "../components/mail/Signatures";
import { MailFiles, useMailFiles } from "../components/MailAttach";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { Archive, BellOff, Ban, Check, ChevronDown, Forward, Image as ImageIcon, Clock, ExternalLink, ListPlus, Mail, Paperclip, PenLine, RefreshCw, Reply, RotateCcw, Send, Sparkles, Star, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, del, post } from "../lib/api";
import { useSpaces } from "../lib/work";
import { cleanEmailHtml, emailSnippet } from "../lib/emailHtml";
import { Card, Empty, ErrorNote, Field, Modal, PageHeader } from "../components/ui";
import { SubProgress } from "../components/work";
import { OutlookAddinCard } from "../components/OutlookAddin";

export interface MailStatus {
  allowed: boolean;
  canSend: boolean;
  canPro?: boolean;
  signature: string;
  microsoft: { available: boolean; connected: boolean; accountEmail: string | null };
  assistant: { on: boolean; level?: string; lastSyncAt?: string | null; error?: string | null };
  counts: { attention: number; vip: number };
}
interface Msg {
  id: string;
  flagged?: boolean;
  flagDue?: string | null;
  categories?: string[];
  fromEmail: string;
  fromName: string;
  subject: string;
  summary: string | null;
  receivedAt: string;
  hasAttachments: boolean;
  webLink: string | null;
  needsMe: boolean | null;
  priority: "vip" | "high" | "normal" | "low";
  state: string;
  doneReason: string | null;
  snoozedUntil: string | null;
  attentionSource: string | null;
  workItemId: string | null;
  workItemRef: string | null;
  workItemSubtasks?: { total: number; done: number } | null;
  draft: string | null;
  draftAuto: boolean;
  pendingSendId: string | null;
  pendingSendAt: string | null;
}

export const useMailStatus = () => useQuery({ queryKey: ["mail", "status"], queryFn: () => api<MailStatus>("/api/mail/status"), staleTime: 60_000, refetchInterval: 120_000 });

const when = (iso: string) => {
  const d = new Date(iso);
  const mins = (Date.now() - d.getTime()) / 60_000;
  if (mins < 60) return `${Math.max(1, Math.round(mins))}m`;
  if (mins < 24 * 60 && d.getDate() === new Date().getDate()) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const snoozeOptions = () => {
  const now = new Date();
  const at = (days: number, h: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() + days);
    d.setHours(h, 0, 0, 0);
    return d;
  };
  const nextMonday = at(((8 - now.getDay()) % 7) || 7, 8);
  return [
    { label: "In 2 hours", at: new Date(now.getTime() + 2 * 3600_000) },
    { label: "Tomorrow 8:00", at: at(1, 8) },
    { label: "Next Monday 8:00", at: nextMonday },
  ];
};
const DONE_LABEL: Record<string, string> = { marked: "Done", replied: "You replied", removed: "Handled in Outlook", task: "Made a task", silenced: "Sender silenced", moved: "Moved to a folder", answered: "Invite answered" };

function TaskDialog({ m, onClose }: { m: Msg; onClose: () => void }) {
  const qc = useQueryClient();
  const spaces = useSpaces();
  const [title, setTitle] = useState(m.subject);
  const [spaceId, setSpaceId] = useState("");
  const [due, setDue] = useState("");
  // Email-derived tasks are personal by default: the private "personal" space (only you see it); a shared workspace is optional.
  const defaultSpace = spaces.data?.items.find((s) => s.key === "personal")?.id ?? spaces.data?.items.find((s) => s.key === "general")?.id ?? spaces.data?.items[0]?.id ?? "";
  const make = useMutation({
    mutationFn: () => post<{ ref: string }>(`/api/mail/messages/${m.id}/task`, { spaceId: spaceId || defaultSpace, title, dueAt: due ? new Date(`${due}T17:00:00`).toISOString() : null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["mail"] });
      onClose();
    },
  });
  return (
    <Modal
      title="Turn into a task"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={make.isPending || !title.trim()} onClick={() => make.mutate()}>{make.isPending ? "Creating…" : "Create task"}</button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Task"><input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Workspace">
            <select className="input" value={spaceId || defaultSpace} onChange={(e) => setSpaceId(e.target.value)}>
              {spaces.data?.items.map((s) => <option key={s.id} value={s.id}>{s.key === "personal" ? "My tasks (private)" : s.name}</option>)}
            </select>
          </Field>
          <Field label="Due (optional)"><input type="date" className="input" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
        </div>
        <p className="muted text-xs">By default it becomes a private task only you can see (Tasks → My tasks), linked back to the email; the email leaves "Needs attention". Pick a workspace to share it with a team instead.</p>
        <ErrorNote error={make.error} />
      </div>
    </Modal>
  );
}


function QueuedBanner({ q, onDone }: { q: { outboxId: string; sendAt: string; label: string; scheduled?: boolean }; onDone: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.ceil((Date.parse(q.sendAt) - now) / 1000));
  useEffect(() => {
    if (left === 0) {
      const t = setTimeout(onDone, 5000);
      return () => clearTimeout(t);
    }
  }, [left, onDone]);
  const undo = useMutation({ mutationFn: () => post(`/api/mail/outbox/${q.outboxId}/cancel`, {}), onSuccess: onDone });
  return (
    <div className="flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-800">
      <Send size={14} /> <span className="truncate">{q.scheduled ? `Scheduled ${q.label} for ${new Date(q.sendAt).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : left > 0 ? `Sending ${q.label} in ${left} s` : `Sent ${q.label}`}</span>
      {left > 0 && <button className="font-semibold underline" disabled={undo.isPending} onClick={() => undo.mutate()}>{q.scheduled ? "Cancel" : "Undo"}</button>}
      {q.scheduled && <button className="ml-auto text-xs underline" onClick={onDone}>Hide</button>}
      <ErrorNote error={undo.error} />
    </div>
  );
}

/** "Sending in 12 s · Undo" while a reply waits in the outbox. */
function PendingSend({ m }: { m: Msg }) {
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, Math.ceil((Date.parse(m.pendingSendAt!) - now) / 1000));
  useEffect(() => {
    if (left === 0) {
      const t = setTimeout(() => void qc.invalidateQueries({ queryKey: ["mail"] }), 6000);
      return () => clearTimeout(t);
    }
  }, [left, qc]);
  const undo = useMutation({ mutationFn: () => post(`/api/mail/outbox/${m.pendingSendId}/cancel`, {}), onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }) });
  return (
    <div className="mt-2 flex items-center gap-2 rounded-lg bg-brand-50 px-3 py-1.5 text-xs text-brand-800">
      <Send size={13} /> {left > 120 ? `Scheduled for ${new Date(m.pendingSendAt!).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : left > 0 ? `Sending in ${left} s` : "Sending…"}
      {left > 0 && <button className="font-semibold underline" disabled={undo.isPending} onClick={() => undo.mutate()}>{left > 120 ? "Cancel" : "Undo"}</button>}
      <ErrorNote error={undo.error} />
    </div>
  );
}

interface ThreadMsg {
  id: string;
  fromName: string;
  fromEmail: string;
  mine: boolean;
  to: string[];
  cc: string[];
  at: string | null;
  isThis: boolean;
  html: string;
  attachments: Array<{ id?: string; name: string; size: number }>;
  webLink: string | null;
}

const kb = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const fullWhen = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function ThreadMessage({ t, open, onToggle, allowImages, rowId }: { t: ThreadMsg; open: boolean; onToggle: () => void; allowImages: boolean; rowId: string }) {
  const clean = useMemo(() => (open ? cleanEmailHtml(t.html, allowImages) : null), [open, t.html, allowImages]);
  const snippet = useMemo(() => (open ? "" : emailSnippet(t.html)), [open, t.html]);
  return (
    <div className={clsx("border-l-2 py-1.5 pl-3", t.mine ? "border-brand-500" : "border-[var(--line)]")}>
      <button type="button" className="flex w-full min-w-0 items-center gap-2 text-left text-sm" onClick={onToggle} aria-expanded={open}>
        <span className={clsx("grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold", t.mine ? "bg-[var(--panel-2)] text-[var(--muted)] ring-1 ring-[var(--line)]" : "bg-brand-50 text-brand-700")}>
          {t.mine ? "You".charAt(0) : (t.fromName || t.fromEmail).charAt(0).toUpperCase()}
        </span>
        <span className="shrink-0 font-semibold">{t.mine ? "You" : t.fromName || t.fromEmail}</span>
        {!open && <span className="muted min-w-0 flex-1 truncate">{snippet}</span>}
        {open && <span className="muted min-w-0 flex-1 truncate text-xs">to {[...t.to, ...t.cc].join(", ") || "—"}</span>}
        <span className="muted shrink-0 text-xs num">{fullWhen(t.at)}</span>
      </button>
      {open && clean && (
        <div className="mt-2">
          <div className="email-html text-sm" dangerouslySetInnerHTML={{ __html: clean.html || "<p><em>(no text)</em></p>" }} />
          {t.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {t.attachments.map((a) => <AttachmentChip key={a.id ?? a.name} messageId={rowId} gid={t.id} a={a} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The whole conversation, oldest first; the newest message is open. Loaded live from Microsoft when expanded. */
function Thread({ m, onSuggest }: { m: Msg; onSuggest: () => void }) {
  const q = useQuery({ queryKey: ["mail", "thread", m.id], queryFn: () => api<{ items: ThreadMsg[] }>(`/api/mail/messages/${m.id}/thread`), staleTime: 60_000 });
  const [openIds, setOpenIds] = useState<Set<string> | null>(null);
  const [allowImages, setAllowImages] = useState(false);
  const items = q.data?.items ?? [];
  const open = openIds ?? new Set(items.length ? [items[items.length - 1]!.id] : []);
  const toggle = (id: string) => setOpenIds(() => {
    const n = new Set(open);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  const anyBlocked = useMemo(() => !allowImages && items.some((t) => open.has(t.id) && /<img[^>]+src=["']?https?:/i.test(t.html)), [allowImages, items, open]);
  const catchUp = useMutation({ mutationFn: () => post<{ summary: string }>(`/api/mail/messages/${m.id}/summary`, {}) });
  if (q.isLoading) return <p className="muted mt-3 text-sm">Loading the conversation from Microsoft 365…</p>;
  if (q.error) return <div className="mt-3"><ErrorNote error={q.error} /></div>;
  return (
    <div className="mt-3 space-y-1 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3">
      <div className="flex items-center gap-2 pb-1 text-xs">
        <span className="muted">{items.length} message{items.length === 1 ? "" : "s"} in this conversation</span>
        {items.length > 1 && <button type="button" className="text-brand-600 hover:underline" onClick={() => setOpenIds(new Set(items.map((t) => t.id)))}>Expand all</button>}
        <button type="button" className="inline-flex items-center gap-1 text-brand-600 hover:underline disabled:opacity-60" disabled={catchUp.isPending} onClick={() => catchUp.mutate()}><Sparkles size={12} /> {catchUp.isPending ? "Reading…" : "Catch me up"}</button>
        {anyBlocked && <button type="button" className="ml-auto inline-flex items-center gap-1 text-brand-600 hover:underline" onClick={() => setAllowImages(true)}><ImageIcon size={12} /> Show images</button>}
      </div>
      {catchUp.data && <div className="mb-2 whitespace-pre-line rounded-lg bg-brand-50 px-3 py-2 text-sm text-brand-900">{catchUp.data.summary}</div>}
      <ErrorNote error={catchUp.error} />
      <MeetingCard messageId={m.id} onSuggest={onSuggest} />
      {items.map((t) => <ThreadMessage key={t.id} t={t} open={open.has(t.id)} onToggle={() => toggle(t.id)} allowImages={allowImages} rowId={m.id} />)}
    </div>
  );
}

function Row({ m, view, canSend }: { m: Msg; view: string; canSend: boolean }) {
  const qc = useQueryClient();
  const [menu, setMenu] = useState<"snooze" | "sender" | "notNeeded" | null>(null);
  const [ruleText, setRuleText] = useState("");
  const [task, setTask] = useState(false);
  const [composing, setComposing] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [suggest, setSuggest] = useState(false); // "Suggest another time" on an invite: the reply opens with Fusion AI proposing slots
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail"] });
  const act = useMutation({ mutationFn: (b: { action: string; until?: string }) => post(`/api/mail/messages/${m.id}/${b.action}`, b.until ? { until: b.until } : {}), onSuccess: refresh });
  const sender = useMutation({ mutationFn: (kind: "vip" | "silence") => post("/api/mail/senders", { kind, pattern: m.fromEmail }), onSuccess: refresh });
  const archive = useMutation({ mutationFn: () => post(`/api/mail/messages/${m.id}/archive`, {}), onSuccess: refresh });
  // "Doesn't need me": just this email, never this sender, or teach a rule — the correction is remembered for tuning.
  const notNeeded = useMutation({
    mutationFn: async (how: "once" | "silence" | "rule") => {
      if (how === "silence") await post("/api/mail/senders", { kind: "silence", pattern: m.fromEmail });
      if (how === "rule") await post("/api/mail/rules", { instruction: ruleText });
      return post(`/api/mail/messages/${m.id}/not_needed`, {});
    },
    onSuccess: refresh,
  });
  return (
    <li className="group border-b border-[var(--line)] px-5 py-3 last:border-0">
      <div className="flex items-start gap-3">
        <div className={clsx("mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold", m.priority === "vip" ? "bg-amber-100 text-amber-700" : "bg-brand-50 text-brand-700")}>
          {(m.fromName || m.fromEmail).charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold">{m.fromName || m.fromEmail}</span>
            {m.priority === "vip" && <span className="chip !border-amber-300 !text-amber-700"><Star size={11} /> VIP</span>}
            {m.priority === "high" && <span className="chip !border-red-300 !text-red-700">High</span>}
            {m.draftAuto && m.draft && !m.pendingSendId && <span className="chip !border-brand-300 !text-brand-700"><PenLine size={11} /> Draft ready</span>}
            <span className="muted ml-auto shrink-0 text-xs num">{when(m.receivedAt)}</span>
            <button type="button" className="muted -my-1 shrink-0 rounded-md p-1 hover:bg-[var(--panel-2)]" aria-expanded={expanded} aria-label={expanded ? "Hide the conversation" : "Show the email and conversation"} onClick={() => setExpanded((x) => !x)}>
              <ChevronDown size={18} className={clsx("transition-transform", expanded && "rotate-180")} />
            </button>
          </div>
          <button type="button" className="block w-full min-w-0 text-left" onClick={() => setExpanded((x) => !x)}>
            <div className="flex items-center gap-1.5 truncate text-sm">
              {m.hasAttachments && <Paperclip size={12} className="muted shrink-0" />}
              <span className="truncate">{m.subject || "(no subject)"}</span>
              <FlagChips m={m} />
            </div>
            {m.summary && <p className="muted mt-0.5 text-sm">{m.summary}</p>}
          </button>
          {expanded && <Thread m={m} onSuggest={() => { setSuggest(true); setComposing(true); setForwarding(false); }} />}
          {m.attentionSource?.startsWith("floor:") && view === "attention" && <p className="mt-0.5 text-xs text-brand-700">Safety net: {m.attentionSource.slice(6)}</p>}
          {view === "done" && <p className="muted mt-0.5 text-xs">{DONE_LABEL[m.doneReason ?? ""] ?? "Done"}{m.workItemRef && m.workItemId ? <> · <Link to="/work/$id" params={{ id: m.workItemId }} className="text-brand-600 hover:underline">{m.workItemRef}</Link>{m.workItemSubtasks && <> · <SubProgress s={m.workItemSubtasks} /></>}</> : null}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {m.webLink && <a className="btn !min-h-0 !px-2 !py-1 text-xs" href={m.webLink} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Open in Outlook</a>}
            {view !== "done" ? (
              <>
                <button className="btn btn-primary !min-h-0 !px-2 !py-1 text-xs" onClick={() => { setComposing((x) => !x); setForwarding(false); }}><Reply size={13} /> {m.draftAuto && m.draft ? "Review draft" : "Reply"}</button>
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => { setForwarding((x) => !x); setComposing(false); }}><Forward size={13} /> Forward</button>
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={act.isPending} onClick={() => act.mutate({ action: "done" })}><Check size={13} /> Done</button>
                {canSend && <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={archive.isPending} title="Move to Archive in Outlook" onClick={() => archive.mutate()}><Archive size={13} /> Archive</button>}
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setMenu(menu === "snooze" ? null : "snooze")}><Clock size={13} /> Snooze</button>
                {!m.workItemId && <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setTask(true)}><ListPlus size={13} /> Make a task</button>}
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setMenu(menu === "sender" ? null : "sender")}>Sender…</button>
                {view === "attention" && <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => setMenu(menu === "notNeeded" ? null : "notNeeded")}><Ban size={13} /> Doesn't need me</button>}
                {canSend && <MoreMenu m={m} />}
              </>
            ) : (
              <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => act.mutate({ action: "reopen" })}><RotateCcw size={13} /> Back to Needs attention</button>
            )}
          </div>
          {menu === "snooze" && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {snoozeOptions().map((o) => <button key={o.label} className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => { act.mutate({ action: "snooze", until: o.at.toISOString() }); setMenu(null); }}>{o.label}</button>)}
            </div>
          )}
          {menu === "notNeeded" && (
            <div className="mt-2 space-y-2 rounded-lg bg-[var(--panel-2)] p-2.5 text-xs">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="muted">Take it off your list:</span>
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={notNeeded.isPending} onClick={() => notNeeded.mutate("once")}>Just this email</button>
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={notNeeded.isPending} onClick={() => notNeeded.mutate("silence")}><BellOff size={13} /> Never from {m.fromEmail}</button>
              </div>
              <form className="flex gap-1.5" onSubmit={(e) => { e.preventDefault(); if (ruleText.trim().length >= 5) notNeeded.mutate("rule"); }}>
                <input className="input !py-1 text-xs" placeholder='Or teach a rule, e.g. "Sales pitches about IT services never need me"' value={ruleText} onChange={(e) => setRuleText(e.target.value)} maxLength={500} />
                <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={ruleText.trim().length < 5 || notNeeded.isPending}>Add rule</button>
              </form>
              <ErrorNote error={notNeeded.error} />
            </div>
          )}
          {menu === "sender" && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => { sender.mutate("vip"); setMenu(null); }}><Star size={13} /> Always important (VIP)</button>
              <button className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={() => { sender.mutate("silence"); setMenu(null); }}><BellOff size={13} /> Never needs me (silence)</button>
              <span className="muted self-center text-xs">{m.fromEmail}</span>
            </div>
          )}
          {m.pendingSendId && m.pendingSendAt && <PendingSend m={m} />}
          {(composing || forwarding) && !m.pendingSendId && (
            <MailComposer key={forwarding ? "fwd" : suggest ? "suggest" : "reply"} initial={forwarding ? "forward" : "reply"}
              autoDraft={suggest && !forwarding ? "Thank them, say the proposed time doesn't work, and suggest two or three other times from my free slots" : undefined} source={{ id: m.id, fromName: m.fromName, fromEmail: m.fromEmail, subject: m.subject, draft: m.draft }}
              canSend={canSend} onClose={() => { setComposing(false); setForwarding(false); setSuggest(false); }} />
          )}
          <ErrorNote error={act.error ?? sender.error ?? archive.error} />
        </div>
      </div>
      {task && <TaskDialog m={m} onClose={() => setTask(false)} />}
    </li>
  );
}

function Setup({ s }: { s: MailStatus }) {
  const qc = useQueryClient();
  const on = useMutation({ mutationFn: () => post("/api/mail/assistant", {}), onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }) });
  if (!s.allowed) return <Card><Empty icon={<Mail size={20} />}>ONE Mail is being rolled out to managers first. It will be offered to you soon.</Empty></Card>;
  return (
    <Card title="Your email assistant" icon={<Mail size={16} />}>
      <div className="space-y-3 text-sm">
        <p>ONE Mail reads your Microsoft 365 inbox and shows only the emails that <strong>need you</strong>, each with a one-line summary. You can mark them done, snooze them, or turn them into a task.</p>
        <ul className="muted list-disc space-y-1 pl-5">
          <li>Only you can see your email in ONE: no admin, manager or CEO view.</li>
          <li>ONE keeps sender, subject and a short summary. The email itself stays in Outlook.</li>
          <li>It doesn't change anything in your mailbox. Keep using Outlook as normal.</li>
          <li>Switch it off any time: everything ONE Mail kept is deleted.</li>
        </ul>
        {!s.microsoft.connected ? (
          <div className="rounded-lg bg-[var(--panel-2)] p-3">
            First connect your Microsoft 365 account. <a className="btn btn-primary ml-2" href="/api/personal/microsoft/connect">Connect Microsoft 365</a>
          </div>
        ) : (
          <button className="btn btn-primary" disabled={on.isPending} onClick={() => on.mutate()}>{on.isPending ? "Switching on…" : `Switch on for ${s.microsoft.accountEmail}`}</button>
        )}
        <ErrorNote error={on.error} />
      </div>
    </Card>
  );
}

function SettingsPanel({ onClose, status }: { onClose: () => void; status: MailStatus }) {
  const qc = useQueryClient();
  const senders = useQuery({ queryKey: ["mail", "senders"], queryFn: () => api<{ items: Array<{ kind: string; pattern: string }> }>("/api/mail/senders") });
  const rules = useQuery({ queryKey: ["mail", "rules"], queryFn: () => api<{ items: Array<{ id: string; instruction: string }> }>("/api/mail/rules") });
  const [pattern, setPattern] = useState("");
  const [kind, setKind] = useState<"vip" | "silence">("vip");
  const [rule, setRule] = useState("");
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail"] });
  const addSender = useMutation({ mutationFn: () => post("/api/mail/senders", { kind, pattern }), onSuccess: () => { setPattern(""); refresh(); } });
  const rmSender = useMutation({ mutationFn: (p: string) => del("/api/mail/senders", { pattern: p }), onSuccess: refresh });
  const addRule = useMutation({ mutationFn: () => post("/api/mail/rules", { instruction: rule }), onSuccess: () => { setRule(""); refresh(); } });
  const rmRule = useMutation({ mutationFn: (id: string) => del(`/api/mail/rules/${id}`), onSuccess: refresh });
  const off = useMutation({ mutationFn: () => del("/api/mail/assistant"), onSuccess: () => { refresh(); onClose(); } });
  const vips = senders.data?.items.filter((x) => x.kind === "vip") ?? [];
  const silenced = senders.data?.items.filter((x) => x.kind === "silence") ?? [];
  const chips = (list: Array<{ pattern: string }>) => (
    <div className="flex flex-wrap gap-1.5">
      {list.length ? list.map((x) => <span key={x.pattern} className="chip">{x.pattern}<button aria-label={`Remove ${x.pattern}`} onClick={() => rmSender.mutate(x.pattern)}><X size={11} /></button></span>) : <span className="muted text-xs">None yet</span>}
    </div>
  );
  return (
    <Modal title="Assistant settings" onClose={onClose} wide>
      <div className="space-y-5 text-sm">
        <section className="space-y-2">
          <h3 className="font-semibold">VIP senders <span className="muted font-normal">(always top of the list)</span></h3>
          {chips(vips)}
          <h3 className="pt-2 font-semibold">Silenced senders <span className="muted font-normal">(never need you)</span></h3>
          {chips(silenced)}
          <form className="flex flex-wrap gap-2 pt-1" onSubmit={(e) => { e.preventDefault(); addSender.mutate(); }}>
            <input className="input !w-64" placeholder="name@company.com or @company.com" value={pattern} onChange={(e) => setPattern(e.target.value)} />
            <select className="input !w-36" value={kind} onChange={(e) => setKind(e.target.value as "vip" | "silence")}><option value="vip">VIP</option><option value="silence">Silence</option></select>
            <button className="btn" disabled={!pattern.trim() || addSender.isPending}>Add</button>
          </form>
          <ErrorNote error={addSender.error} />
        </section>
        <section className="line space-y-2 border-t pt-4">
          <h3 className="font-semibold">Your rules <span className="muted font-normal">(plain English, the assistant follows them)</span></h3>
          <ul className="space-y-1">
            {rules.data?.items.map((r) => <li key={r.id} className="flex items-start gap-2"><span className="flex-1">{r.instruction}</span><button className="muted" aria-label="Remove rule" onClick={() => rmRule.mutate(r.id)}><X size={14} /></button></li>)}
          </ul>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); addRule.mutate(); }}>
            <input className="input" placeholder='e.g. "Invoices from AWS never need me"' value={rule} onChange={(e) => setRule(e.target.value)} maxLength={500} />
            <button className="btn" disabled={rule.trim().length < 5 || addRule.isPending}>Add</button>
          </form>
          <p className="muted text-xs">Rules apply to new email from now on.</p>
        </section>
        <section className="line space-y-2 border-t pt-4">
          <h3 className="font-semibold">Replying from ONE</h3>
          {status.canSend ? (
            <p className="text-emerald-700">Allowed: you can reply, reply all and archive from ONE.</p>
          ) : (
            <p>Not allowed yet. <a className="btn btn-primary ml-1" href="/api/personal/microsoft/connect?send=1">Allow replying from ONE</a> <span className="muted text-xs">Microsoft will ask you to approve sending and moving mail.</span></p>
          )}
          <Field label="Signatures (added under your emails; switch per email when you write)">
            <Signatures />
          </Field>
          <Field label="Quick replies (insert them from the composer)">
            <Templates />
          </Field>
        </section>
        <section className="line space-y-3 border-t pt-4">
          <h3 className="text-sm font-semibold">Mail Pro</h3>
          <MailProSwitch canPro={!!status.canPro} />
          <Field label="Out-of-office"><OutOfOffice /></Field>
          <Field label="Shared mailboxes (read them and reply as them in ONE)"><SharedMailboxes canPro={!!status.canPro} /></Field>
        </section>
        <section className="line border-t pt-4">
          <button className="btn btn-danger" disabled={off.isPending} onClick={() => { if (confirm("Switch off ONE Mail? Everything it kept about your email (summaries, rules, VIPs) is deleted. Outlook is not affected.")) off.mutate(); }}>Switch off and delete my ONE Mail data</button>
        </section>
      </div>
    </Modal>
  );
}

interface Followup { id: string; messageId: string; sentAt: string; dueAt: string; subject: string; fromName: string; fromEmail: string; webLink: string | null }

/** Replies you asked to be reminded about: they come back to Needs attention if nobody answers. */
/** Emails set to go later (Send later), with Cancel. */
function ScheduledList() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail", "scheduled"], queryFn: () => api<{ items: Array<{ id: string; sendAt: string; subject: string | null; toName: string | null }> }>("/api/mail/scheduled"), refetchInterval: 60_000 });
  const cancel = useMutation({ mutationFn: (id: string) => post(`/api/mail/outbox/${id}/cancel`, {}), onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }) });
  if (!q.data?.items.length) return null;
  return (
    <div className="border-b border-[var(--line)] px-3 py-2">
      <div className="section-label mb-1">Scheduled to send</div>
      <ul className="space-y-1">
        {q.data.items.map((o) => (
          <li key={o.id} className="flex items-center gap-2 text-sm">
            <Send size={13} className="muted shrink-0" />
            <span className="min-w-0 flex-1 truncate">{o.subject || "(no subject)"} <span className="muted text-xs">to {o.toName}</span></span>
            <span className="muted shrink-0 text-xs">{new Date(o.sendAt).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <button type="button" className="btn btn-sm" disabled={cancel.isPending} onClick={() => cancel.mutate(o.id)}>Cancel</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WaitingList() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail", "followups"], queryFn: () => api<{ items: Followup[] }>("/api/mail/followups"), refetchInterval: 120_000 });
  const cancel = useMutation({ mutationFn: (id: string) => del(`/api/mail/followups/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["mail", "followups"] }) });
  if (q.isLoading) return <Empty>Loading…</Empty>;
  if (!q.data?.items.length) return <><ScheduledList /><Empty icon={<Clock size={20} />}>No reminders. When you reply from ONE, pick "Remind me if no reply" and the email comes back here if nobody answers.</Empty></>;
  return (
    <>
    <ScheduledList />
    <ul>
      {q.data.items.map((f) => (
        <li key={f.id} className="flex items-center gap-3 border-b border-[var(--line)] px-5 py-3 text-sm last:border-0">
          <Clock size={16} className="muted shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{f.fromName || f.fromEmail}</div>
            <div className="truncate">{f.subject}</div>
            <div className="muted text-xs">You replied {fullWhen(f.sentAt)} · comes back {fullWhen(f.dueAt)} if no answer</div>
          </div>
          {f.webLink && <a className="btn !min-h-0 !px-2 !py-1 text-xs" href={f.webLink} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Outlook</a>}
          <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={cancel.isPending} onClick={() => cancel.mutate(f.id)}>Cancel reminder</button>
        </li>
      ))}
    </ul>
    </>
  );
}

/** "Silence this sender?" — learned from your corrections and from senders piling up unanswered. */
function Suggestions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail", "suggestions"], queryFn: () => api<{ items: Array<{ fromEmail: string; fromName: string; kind: "repeat" | "noisy"; count: number }> }>("/api/mail/suggestions"), staleTime: 60_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail"] });
  const silence = useMutation({ mutationFn: (e: string) => post("/api/mail/senders", { kind: "silence", pattern: e }), onSuccess: refresh });
  const keep = useMutation({ mutationFn: (e: string) => post("/api/mail/suggestions/keep", { fromEmail: e }), onSuccess: refresh });
  const items = q.data?.items ?? [];
  if (!items.length) return null;
  return (
    <div className="rounded-xl border border-[var(--line)] bg-[var(--panel)] px-4 py-3 text-sm">
      <div className="mb-2 font-semibold">Tidy your list</div>
      <ul className="space-y-1.5">
        {items.map((x) => (
          <li key={x.fromEmail} className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 flex-1">
              <strong>{x.fromName || x.fromEmail}</strong>{" "}
              <span className="muted">{x.kind === "repeat" ? `— you said "doesn't need me" ${x.count} times` : `— ${x.count} emails waiting, none answered`}</span>
            </span>
            <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={silence.isPending} onClick={() => silence.mutate(x.fromEmail)}><BellOff size={13} /> Silence</button>
            <button className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={keep.isPending} onClick={() => keep.mutate(x.fromEmail)}>Keep showing</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MailPage() {
  const status = useMailStatus();
  const qc = useQueryClient();
  // ?q= (from search everything): open Recent already searched for that email
  const urlQ = (() => { try { return new URLSearchParams(location.search).get("q") ?? ""; } catch { return ""; } })();
  const [view, setView] = useState<string>(urlQ ? "recent" : "attention"); // also "sent" and "shared:<address>"
  const [filter, setFilter] = useState<MailFilter>({});
  const [showFilters, setShowFilters] = useState(false);
  const shared = useQuery({ queryKey: ["mail-shared"], queryFn: () => api<{ items: Array<{ address: string; name: string | null }> }>("/api/mail/shared") });
  const [q, setQ] = useState(urlQ);
  const [settings, setSettings] = useState(false);
  const [composeNew, setComposeNew] = useState(false);
  const [queued, setQueued] = useState<{ outboxId: string; sendAt: string; label: string; scheduled?: boolean } | null>(null);
  const clearQueued = useCallback(() => setQueued(null), []);
  const on = !!status.data?.assistant.on;
  const list = useQuery({
    queryKey: ["mail", "list", view, q, filter],
    queryFn: () => api<{ items: Msg[] }>(`/api/mail/messages?view=${view}${q ? `&q=${encodeURIComponent(q)}` : ""}${filterQs(filter)}`),
    enabled: on && ["attention", "recent", "done"].includes(view),
    refetchInterval: 60_000,
  });
  const sync = useMutation({ mutationFn: () => post("/api/mail/sync", {}), onSuccess: () => qc.invalidateQueries({ queryKey: ["mail"] }) });
  const s = status.data;
  const tabs = useMemo(() => [
    { key: "attention" as const, label: "Needs attention", count: s?.counts.attention },
    { key: "waiting" as const, label: "Waiting for reply" },
    { key: "recent" as const, label: "All recent" },
    { key: "done" as const, label: "Done" },
    { key: "sent", label: "Sent" },
    ...(shared.data?.items ?? []).map((b) => ({ key: `shared:${b.address}`, label: b.name || b.address, count: undefined as number | undefined })),
  ] as Array<{ key: string; label: string; count?: number }>, [s?.counts.attention, shared.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mail"
        subtitle={on ? `Your Microsoft 365 inbox, triaged${s?.microsoft.accountEmail ? ` · ${s.microsoft.accountEmail}` : ""}${s?.assistant.lastSyncAt ? ` · checked ${when(s.assistant.lastSyncAt)} ago` : ""}` : "Your email assistant"}
        actions={on && (
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={() => setComposeNew(true)}><PenLine size={15} /> New email</button>
            <button className="btn" disabled={sync.isPending} onClick={() => sync.mutate()}><RefreshCw size={15} className={sync.isPending ? "animate-spin" : ""} /> Check now</button>
            <button className="btn" onClick={() => setSettings(true)}>Settings</button>
          </div>
        )}
      />
      {status.isLoading ? <Empty>Loading…</Empty> : !s ? <ErrorNote error={status.error} /> : !on ? <Setup s={s} /> : (
        <>
          {s.assistant.error && <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Last check failed: {s.assistant.error}. <a className="font-medium underline" href="/api/personal/microsoft/connect">Reconnect Microsoft 365</a></div>}
          <ErrorNote error={sync.error} />
          {queued && <QueuedBanner key={queued.outboxId} q={queued} onDone={clearQueued} />}
          <div className="flex flex-wrap items-center gap-2">
            {tabs.map((t) => (
              <button key={t.key} className={clsx("btn", view === t.key && "btn-primary")} onClick={() => setView(t.key)}>
                {t.label}{t.count ? <span className="num ml-1 rounded-full bg-white/25 px-1.5 text-xs">{t.count}</span> : null}
              </button>
            ))}
            <input className="input ml-auto !w-64" placeholder="Search sender, subject, summary" value={q} onChange={(e) => setQ(e.target.value)} />
            {["attention", "recent", "done"].includes(view) && <button className={clsx("btn", showFilters && "btn-primary")} onClick={() => setShowFilters((v) => !v)} aria-pressed={showFilters}>Filters</button>}
          </div>
          {showFilters && ["attention", "recent", "done"].includes(view) && <FilterBar value={filter} onChange={setFilter} />}
          <OooBanner />
          {view === "attention" && <Suggestions />}
          <Card flush>
            {view === "sent" ? <SentList q={q} /> : view.startsWith("shared:") ? <SharedInbox address={view.slice(7)} canSend={!!s.canSend} q={q} /> : view === "waiting" ? <WaitingList /> : list.isLoading ? <Empty>Loading…</Empty> : list.error ? <ErrorNote error={list.error} /> : !list.data?.items.length ? (
              <Empty icon={<Check size={20} />}>{view === "attention" ? "Nothing needs you right now. New email is checked every minute." : "Nothing here yet."}</Empty>
            ) : (
              <ul>{list.data.items.map((m) => <Row key={m.id} m={m} view={view} canSend={!!s.canSend} />)}</ul>
            )}
          </Card>
        </>
      )}
      <OutlookAddinCard />
      {settings && s && <SettingsPanel status={s} onClose={() => setSettings(false)} />}
      {composeNew && s && (
        <Modal title="New email" wide onClose={() => setComposeNew(false)}>
          <MailComposer initial="new" canSend={!!s.canSend} onClose={() => setComposeNew(false)} onQueued={setQueued} />
        </Modal>
      )}
    </div>
  );
}

/** My Day: the top emails that need you (only when the assistant is on). */
export function MailCard() {
  const status = useMailStatus();
  const on = !!status.data?.assistant.on;
  const list = useQuery({ queryKey: ["mail", "list", "attention", ""], queryFn: () => api<{ items: Msg[] }>("/api/mail/messages?view=attention"), enabled: on, refetchInterval: 60_000 });
  if (!status.data?.allowed) return null;
  if (!on) {
    return (
      <Card title="Email that needs you" icon={<Mail size={16} />}>
        <p className="muted mb-3 text-sm">Let ONE Mail read your inbox and show only the emails that need you.</p>
        <Link to="/mail" className="btn btn-primary">Set up ONE Mail</Link>
      </Card>
    );
  }
  const items = list.data?.items ?? [];
  return (
    <Card title="Email that needs you" icon={<Mail size={16} />} count={status.data?.counts.attention} actions={<Link to="/mail" className="text-xs font-semibold text-brand-600 hover:underline">Open Mail</Link>} flush>
      {items.length ? (
        <ul>
          {items.slice(0, 5).map((m) => (
            <li key={m.id} className="border-b border-[var(--line)] px-5 py-2.5 last:border-0">
              <Link to="/mail" className="block hover:opacity-80">
                <div className="flex items-baseline gap-2 text-sm">
                  {m.priority === "vip" && <Star size={12} className="shrink-0 text-amber-500" />}
                  <span className="truncate font-semibold">{m.fromName || m.fromEmail}</span>
                  <span className="muted ml-auto shrink-0 text-xs num">{when(m.receivedAt)}</span>
                </div>
                <p className="muted truncate text-xs">{m.summary || m.subject}</p>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted px-5 pb-3 text-sm">Nothing needs you right now.</p>
      )}
    </Card>
  );
}
